import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { Radio, RadioGroup, } from '@fluentui/react-components';
import { DefaultButton } from '@bingads-webui-react/fabric-control';
import { UploadProgress } from '@bingads-webui-campaign-react/upload-file-picker';
import {
  EncodingPlaceholderWithStyle,
  UnverifiedPlaceholderWithStyle,
  FailedPlaceholderWithStyle,
} from '@bingads-webui-campaign-react/video-status-placeholder';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { ImageExperienceVideo } from '../model/image-experience-video';
import { getMultiVideoListViewStyles } from '../../style/js/multi-video-list-view-styles';

const DisplayPlusMaxVideoCount = 5;

export const MultiVideoListView = ({
  i18n,
  onCancelUpload,
  isVideoUploading,
  totalBytes,
  uploadedBytes,
  onVideoSelect,
  videoList,
  CustomAddVideoControl,
  onAddVideo,
  onRenderItemDropdown,
}) => {
  const [selectedVideoIdx, setSelectedVideoIdx] = useState(null);

  const classes = useDefaultStyles(getMultiVideoListViewStyles);

  const onThumbnailClick = (idxString) => {
    const idx = parseInt(idxString, 10);
    if (!_.isNaN(idx)) {
      setSelectedVideoIdx(idx);
      if (onVideoSelect) {
        onVideoSelect(idx);
      }
    }
  };

  const renderVideoComponent = (video, idx) => {
    let component = null;

    if (video.status === 'Active') {
      component = (
        <div className={classNames(classes.videoContainerStyle, 'active-video')}>
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src={video.ThumbnailUrl}
              alt={i18n.getString('ImageExperience_Video_Thumbnail')}
              className={classNames(classes.videoImageStyle, 'video-thumbnail-image')}
            />
            <div className={classes.hoverButtonsStyle}>
              {onRenderItemDropdown(videoList[idx], idx)}
            </div>
          </div>
        </div>
      );
    } else if (video.status === 'Draft' || video.status === 'Transcoding') {
      component = (
        <div className={classNames(classes.videoThumbnail, 'draft-video')}>
          <EncodingPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Encoding')} textColor="white" />
          <div className={classes.hoverButtonsStyle}>
            {onRenderItemDropdown(videoList[idx], idx)}
          </div>
        </div>
      );
    } else if (video.status === 'Unverified') {
      component = (
        <div className={classNames(classes.videoThumbnail, 'unverified-video')}>
          <UnverifiedPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Unverified')} textColor="white" />
          <div className={classes.hoverButtonsStyle}>
            {onRenderItemDropdown(videoList[idx], idx)}
          </div>
        </div>
      );
    } else if (video.status === 'Failed') {
      component = (
        <div className={classNames(classes.videoThumbnail, 'failed-video')}>
          <FailedPlaceholderWithStyle placeholderContent={i18n.getString('ImageExperience_Video_Status_Failed')} textColor="white" />
          <div className={classes.hoverButtonsStyle}>
            {onRenderItemDropdown(videoList[idx], idx)}
          </div>
        </div>
      );
    }

    return component;
  };

  return (
    <div className="image-list-view-content">
      <RadioGroup
        className={classes.radioGroupContainer}
        layout="horizontal"
        value={selectedVideoIdx}
        onChange={(ev, data) => onThumbnailClick(data.value)}
      >
        {_.map(videoList, (video, idx) => (
          <Radio
            className={classNames(classes.radioOption, selectedVideoIdx === idx && 'selected')}
            key={idx}
            value={idx}
            label={renderVideoComponent(video, idx)}
          />
        ))}
        {
          videoList.length < DisplayPlusMaxVideoCount &&
          <Radio
            className={classes.radioOptionHidden}
            key="add-video"
            label={<CustomAddVideoControl onClick={onAddVideo} multiVideoMode />}
          />
        }
      </RadioGroup>
      {
        isVideoUploading &&
        <div className={classNames(classes.uploadingContainer, 'uploading-video')}>
          <UploadProgress
            totalBytes={totalBytes}
            uploadedBytes={uploadedBytes}
            i18n={i18n}
            type="circle"
          />
          <div className="cancel-upload-button-container">
            <DefaultButton
              className="cancel-upload-button"
              ariaLabel={i18n.getString('Audience_Campaign_Cancel_Label')}
              text={i18n.getString('Audience_Campaign_Cancel_Label')}
              onClick={onCancelUpload}
            />
          </div>
        </div>
      }
    </div>
  );
};

MultiVideoListView.propTypes = {
  videoList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceVideo)).isRequired,
  onCancelUpload: PropTypes.func.isRequired,
  onVideoSelect: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  CustomAddVideoControl: PropTypes.elementType.isRequired,
  onAddVideo: PropTypes.func.isRequired,
  onRenderItemDropdown: PropTypes.func.isRequired,
  isVideoUploading: PropTypes.bool.isRequired,
  totalBytes: PropTypes.number,
  uploadedBytes: PropTypes.number,
};

MultiVideoListView.defaultProps = {
  onVideoSelect: null,
  totalBytes: 0,
  uploadedBytes: 0,
};
